import React from "react";
import { Box, Typography, createStyles, withStyles } from "@material-ui/core";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import { magicLinkColors, magicLinkRadius } from "./magicLinkTokens";

const STEPS = [
  "Analyzing your answers...",
  "Matching services...",
  "Generating your packages...",
];

const DOT_COUNT = 8;
const STEP_DELAY = 1200;

export const GENERATING_ANIMATION_MS = STEP_DELAY * STEPS.length;

interface Props {
  classes: Record<string, string>;
}

interface State {
  visibleSteps: number;
}

const styles = (theme: any) =>
  createStyles({
    "@keyframes magicLinkSpinnerDot": {
      "0%": {
        transform: "scale(1)",
        backgroundColor: magicLinkColors.disabled,
      },
      "10%": {
        transform: "scale(3)",
        backgroundColor: theme.palette.primary.main,
      },
      "40%, 100%": {
        transform: "scale(1)",
        backgroundColor: magicLinkColors.disabled,
      },
    },
    card: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 24,
      padding: "48px 24px",
      borderRadius: magicLinkRadius.card,
      backgroundColor: magicLinkColors.surface,
    },
    title: {
      fontFamily: "Nunito Sans",
      fontSize: "24px",
      fontWeight: 600,
      lineHeight: 1,
      color: magicLinkColors.textStrong,
      textAlign: "center",
    },
    spinner: {
      position: "relative",
      width: 69,
      height: 69,
    },
    dotSlot: {
      position: "absolute",
      top: "50%",
      left: "50%",
      width: 6,
      height: 6,
      marginTop: -3,
      marginLeft: -3,
    },
    dot: {
      display: "block",
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      backgroundColor: magicLinkColors.disabled,
      animation: `$magicLinkSpinnerDot ${DOT_COUNT * 150}ms linear infinite`,
    },
    steps: {
      display: "flex",
      flexDirection: "column",
      gap: 16,
    },
    step: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontFamily: "Nunito Sans",
      fontSize: "12px",
      fontWeight: 600,
      lineHeight: "16px",
      color: theme.palette.primary.main,
    },
    stepIcon: {
      fontSize: 18,
    },
  });

class GeneratingPackage extends React.Component<Props, State> {
  state: State = { visibleSteps: 1 };
  timers: number[] = [];

  componentDidMount() {
    STEPS.slice(1).forEach((_, index) => {
      this.timers.push(
        window.setTimeout(
          () => this.setState({ visibleSteps: index + 2 }),
          STEP_DELAY * (index + 1)
        )
      );
    });
  }

  componentWillUnmount() {
    this.timers.forEach((timer) => window.clearTimeout(timer));
  }

  render() {
    const { classes } = this.props;
    const radius = 31.5;
    return (
      <Box className={classes.card} role="status" aria-live="polite">
        <Typography component="h2" className={classes.title}>
          Generating your package
        </Typography>
        <Box className={classes.spinner} aria-hidden="true">
          {Array.from({ length: DOT_COUNT }).map((_, index) => {
            const angle = (index / DOT_COUNT) * 2 * Math.PI - Math.PI / 2;
            return (
              <span
                key={index}
                className={classes.dotSlot}
                style={{
                  transform: `translate(${Math.cos(angle) * radius}px, ${Math.sin(angle) * radius}px)`,
                }}
              >
                <span
                  className={classes.dot}
                  style={{ animationDelay: `${index * 150}ms` }}
                />
              </span>
            );
          })}
        </Box>
        <Box className={classes.steps}>
          {STEPS.slice(0, this.state.visibleSteps).map((step) => (
            <Typography key={step} component="p" className={classes.step}>
              <CheckCircleOutlineIcon className={classes.stepIcon} />
              {step}
            </Typography>
          ))}
        </Box>
      </Box>
    );
  }
}

export default withStyles(styles)(GeneratingPackage);
